import { useQuery } from '@tanstack/react-query';
import { getTimeTillEndOfDayMs } from '@toggle/helpers';
import { ColumnMeta, FilterResults, screener } from '@toggle/toggle';
import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { WatchlistEntities } from '~/stores/use-watchlist/watchlist-types';
import { wretchRequest } from '~/utils/api-fetch/apiFetch';
import {
  EntityPlusPrice,
  useAssetsTableData,
} from '~/views/dashboard/my-assets-widget/assets-table/useAssetsTableData';

export const DEFAULT_TAB = 'Overview';
const QUERY_KEY_WATCHLIST_TABLE = 'QUERY_KEY_WATCHLIST_TABLE';

const getOverviewColumnsMeta = (t: TFunction) => {
  const defaultFields = {
    value_type: 'float',
    directional: true,
  };

  return [
    {
      field: 'return1D',
      name: t('widget:watchlist.oneDayChange'),
      ...defaultFields,
    },
    {
      field: 'return1W',
      name: t('widget:watchlist.oneWeekChange'),
      ...defaultFields,
    },
    {
      field: 'return1M',
      name: t('widget:watchlist.oneMonthChange'),
      ...defaultFields,
    },
    {
      field: 'return3M',
      name: t('widget:watchlist.threeMonthsChange'),
      ...defaultFields,
    },
  ] as ColumnMeta[];
};

export const useWatchlistTable = ({
  entities,
  tab = DEFAULT_TAB,
  isWatchlistPage = false,
}: {
  entities: WatchlistEntities[];
  tab?: string;
  isWatchlistPage?: boolean;
}) => {
  const { t } = useTranslation('widget');

  const requestFilters = useMemo(
    () => ({
      entity_tag: {
        children: [],
        values: [{ tags: entities.map(e => e.tag) }],
      },
    }),
    [entities]
  );

  const { data: overviewTabData } = useAssetsTableData(entities);
  const filterResultQueryResult = useQuery({
    queryKey: [QUERY_KEY_WATCHLIST_TABLE, requestFilters],
    queryFn: async ({ signal }) => {
      const filterResults = wretchRequest<FilterResults>(
        screener.postFilter.path,
        {
          method: 'post',
          body: {
            filters: requestFilters,
          },
          signal,
        }
      );

      return filterResults;
    },
    staleTime: getTimeTillEndOfDayMs(),
    enabled: isWatchlistPage,
  });

  const isOverviewTab = tab === 'Overview';
  const data = filterResultQueryResult.data;
  const categoryData = data?.data.find(item => item.category === tab);
  const columnsMeta = useMemo(
    () =>
      isOverviewTab ? getOverviewColumnsMeta(t) : categoryData?.meta ?? [],
    [categoryData?.meta, tab]
  );
  const watchlistTableData = useMemo(() => {
    if (isOverviewTab) {
      return overviewTabData;
    }

    if (categoryData?.data) {
      return entities.reduce((res, item) => {
        const index = data!.entities.findIndex(tag => tag === item.tag);
        if (index > -1) {
          const tableRow = {
            asset: item.asset,
            isLoading: item.isLoading,
            tag: item.tag,
            ...categoryData.data[index],
          } as EntityPlusPrice;
          res.push(tableRow);
        }

        return res;
      }, [] as EntityPlusPrice[]);
    }

    return [];
  }, [tab, data, entities]);

  return {
    filterResultQueryResult,
    columnsMeta,
    watchlistTableData,
    categories: data?.data.map(c => c.category) ?? [],
  };
};
